'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { get, set } from 'es-toolkit/compat';

import type { IObjectTreeNode, ISceneTree } from '@/editor/store/models';
import type * as THREE from 'three';

import { findActorFromObject } from '@/editor/core/objectHelpers';
import { editorStore } from '@/editor/store';

const DEBUG = {
  logSetUIProperty: false
} as const;

/**
 * The UniversalAccessor is a class that provides a way to access and modify objects in the editor.
 * In short, it allows you to create an-object agnostic path to an object in the editor, including nested components and properties.
 * Later, this can be used to re-access the original asset, regardless of selection state or other changes.
 */
export class UniversalAccessor {
  constructor(
    public readonly actorId: string,
    public readonly componentPath?: string,
    public readonly propertyPath?: string,
    public readonly isInvalid: boolean = false,
    public readonly resourcePath?: string,
    public readonly isWorld: boolean = false,
    public readonly materialPath?: string,
  ) { }

  /**
   * Creates a string representation of the accessor path
   */
  toString(): string {
    // World accessor format: world/property:propertyPath
    if (this.isWorld) {
      let path = 'world';
      if (this.propertyPath) {
        path += `/property:${this.propertyPath}`;
      }
      return path;
    }

    // Resource accessor format: resource:path/to/file.res.json/property:propertyPath
    if (this.resourcePath) {
      let path = `resource:${this.resourcePath}`;
      if (this.propertyPath) {
        path += `/property:${this.propertyPath}`;
      }
      return path;
    }

    // Material accessor format: material:path/to/file.mat.json/property:propertyPath
    if (this.materialPath) {
      let path = `material:${this.materialPath}`;
      if (this.propertyPath) {
        path += `/property:${this.propertyPath}`;
      }
      return path;
    }

    let path = `actor:${this.actorId}`;
    if (this.componentPath && this.componentPath !== 'material') {
      path += `/component:${this.componentPath}`;
    }
    if (this.propertyPath) {
      path += `/property:${this.propertyPath}`;
    }
    return path;
  }

  /**
   * Creates a UniversalAccessor from a string path
   */
  static fromString(path: string): UniversalAccessor {
    // Try world pattern first
    const worldMatch = path.match(/^world(?:\/property:(.+))?$/);
    if (worldMatch) {
      return new UniversalAccessor(
        '',
        undefined,
        worldMatch[1],
        false,
        undefined,
        true
      );
    }

    // Try resource pattern
    const resourceMatch = path.match(/^resource:([^/]+(?:\/[^/]+)*)(?:\/property:(.+))?$/);
    if (resourceMatch) {
      return new UniversalAccessor(
        '',
        undefined,
        resourceMatch[2],
        false,
        resourceMatch[1]
      );
    }

    // Try material pattern (path may contain slashes, terminated by /property:)
    const materialMatch = path.match(/^material:(.*?)(?:\/property:(.+))?$/);
    if (materialMatch) {
      return new UniversalAccessor(
        '',
        undefined,
        materialMatch[2],
        false,
        undefined,
        false,
        materialMatch[1],
      );
    }

    // Try actor pattern
    const actorMatch = path.match(/^actor:([^/]+)(?:\/component:([^/]+))?(?:\/property:(.+))?$/);
    if (actorMatch) {
      return new UniversalAccessor(
        actorMatch[1],
        actorMatch[2],
        actorMatch[3]
      );
    }

    return UniversalAccessor.createInvalid();
  }

  /**
   * Creates a UniversalAccessor for an actor only
   */
  static forActor(actorId: string): UniversalAccessor {
    return new UniversalAccessor(actorId);
  }

  /**
   * Creates a UniversalAccessor for a component within an actor
   */
  static forComponent(actorId: string, componentId: string): UniversalAccessor {
    return new UniversalAccessor(actorId, componentId);
  }

  /**
   * Creates a UniversalAccessor for a property on a component
   */
  static forProperty(actorId: string, componentId: string, propertyPath: string): UniversalAccessor {
    return new UniversalAccessor(actorId, componentId, propertyPath);
  }

  /**
   * Creates a UniversalAccessor for a property on an actor
   */
  static forActorProperty(actorId: string, propertyPath: string): UniversalAccessor {
    return new UniversalAccessor(actorId, undefined, propertyPath);
  }

  /**
   * Creates a UniversalAccessor for a property on a material asset, identified by its asset path.
   *
   * @param materialPath - The asset file path (e.g. `assets/materials/red.mat.json`).
   * @param propertyPath - The property path within the material.
   */
  static forMaterialProperty(materialPath: string, propertyPath: string): UniversalAccessor {
    return new UniversalAccessor('', undefined, propertyPath, false, undefined, false, materialPath);
  }

  /**
   * Creates a UniversalAccessor for a material asset, identified by its asset path.
   *
   * @param materialPath - The asset file path (e.g. `assets/materials/red.mat.json`).
   */
  static forMaterial(materialPath: string): UniversalAccessor {
    return new UniversalAccessor('', undefined, undefined, false, undefined, false, materialPath);
  }

  /**
   * Creates a UniversalAccessor for a resource asset
   */
  static forResource(resourcePath: string): UniversalAccessor {
    return new UniversalAccessor('', undefined, undefined, false, resourcePath);
  }

  /**
   * Creates a UniversalAccessor for a property on a resource asset
   */
  static forResourceProperty(resourcePath: string, propertyPath: string): UniversalAccessor {
    return new UniversalAccessor('', undefined, propertyPath, false, resourcePath);
  }

  /**
   * Creates a UniversalAccessor for the world
   */
  static forWorld(): UniversalAccessor {
    return new UniversalAccessor('', undefined, undefined, false, undefined, true);
  }

  /**
   * Creates a UniversalAccessor for a property on the world
   */
  static forWorldProperty(propertyPath: string): UniversalAccessor {
    return new UniversalAccessor('', undefined, propertyPath, false, undefined, true);
  }

  /**
   * Creates an invalid UniversalAccessor that will safely do nothing when operations are performed on it
   */
  static createInvalid(actorId: string = '', componentPath?: string, propertyPath?: string, resourcePath?: string): UniversalAccessor {
    return new UniversalAccessor(actorId, componentPath, propertyPath, true, resourcePath);
  }

  /**
   * Resolves the accessor to find the target actor in the current world
   */
  resolveActor(): ENGINE.Actor | null {
    if (this.isInvalid) return null;

    const world = editorStore.scene.world;
    if (!world) return null;

    // First try to find in world (normal case)
    const worldActor = world.getActorsByPredicate((actor: ENGINE.Actor) => actor.uuid === this.actorId)[0];
    if (worldActor) return worldActor;

    // TODO: This logic is for handling prefabs, but it needs to be much more robust.
    const selectedObjects = editorStore.scene.selectedObjects;
    for (const selectedObject of selectedObjects) {
      const actor = selectedObject.asActor;
      if (actor && actor.uuid === this.actorId) {
        return actor;
      }
    }

    return null;
  }

  /**
   * Resolves the accessor to find the target component in the current world
   */
  resolveComponent(): ENGINE.SceneComponent | null {
    if (this.isInvalid) return null;
    if (!this.componentPath) return null;
    if (this.materialPath) return null; // Materials don't have components

    const actor = this.resolveActor();
    if (!actor) return null;

    return this.findComponentInActor(actor, this.componentPath);
  }

  /**
   * Resolves the accessor to a plain `THREE.Object3D` under the target actor's hierarchy.
   * Use this for gizmo targets that are not `SceneComponent` instances (e.g. raw meshes).
   */
  resolveObject3D(): THREE.Object3D | null {
    if (this.isInvalid) return null;
    if (!this.componentPath) return null;

    const actor = this.resolveActor();
    const root = actor?.rootComponent;
    if (!root) return null;

    let found: THREE.Object3D | null = null;
    root.traverse(obj => {
      if (obj.uuid === this.componentPath) {
        found = obj;
      }
    });
    return found;
  }

  /**
   * Resolves the accessor to find the target material.
   * Matches by asset path (via `editable.path`) so the reference stays valid across undo/redo,
   * which may reload the material and assign a new Three.js uuid.
   */
  resolveMaterial(): any | null {
    if (this.isInvalid) return null;
    if (!this.materialPath) return null;

    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (inspectorPropertyStore) {
      const materialSource = inspectorPropertyStore.sources.find(source =>
        source.editable && 'path' in source.editable && source.editable.path === this.materialPath
      );
      return materialSource ? materialSource.target : null;
    }

    return null;
  }

  /**
   * Resolves the accessor to find the target resource
   */
  resolveResource(): any | null {
    if (this.isInvalid) return null;
    if (!this.resourcePath) return null;

    // Resources are accessed through the inspector panel's property store (just like materials)
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (inspectorPropertyStore) {
      const resourceSource = inspectorPropertyStore.sources.find(source => {
        // Check if this is a ResourceData object for the matching asset path
        if (source.editable && 'path' in source.editable &&
          source.target && typeof source.target === 'object' &&
          'instance' in source.target) {
          return source.editable.path === this.resourcePath;
        }
        return false;
      });
      // Return the instance, not the ResourceData wrapper (just like materials return the material, not a wrapper)
      return resourceSource ? (resourceSource.target as any).instance : null;
    }

    return null;
  }

  /**
   * Resolves the accessor to find the target world
   */
  resolveWorld(): ENGINE.World | null {
    if (this.isInvalid) return null;
    if (!this.isWorld) return null;

    return editorStore.scene.world;
  }

  /**
   * Resolves the accessor to get the target property value
   */
  resolveProperty(): any {
    if (this.isInvalid) return null;
    if (!this.propertyPath) return null;

    if (this.isWorld) {
      const world = this.resolveWorld();
      if (!world) return null;
      return get(world, this.propertyPath);
    } else if (this.resourcePath) {
      const resource = this.resolveResource();
      if (!resource) return null;
      return get(resource, this.propertyPath);
    } else if (this.materialPath) {
      const material = this.resolveMaterial();
      if (!material) return null;
      return get(material, this.propertyPath);
    } else if (this.componentPath) {
      const component = this.resolveComponent();
      if (!component) return null;
      return get(component, this.propertyPath);
    } else {
      const actor = this.resolveActor();
      if (!actor) return null;
      return get(actor, this.propertyPath);
    }
  }

  /**
   * Sets the target property value
   */
  setProperty(value: any): boolean {
    if (this.isInvalid) return false;
    if (!this.propertyPath) return false;

    if (this.isWorld) {
      const world = this.resolveWorld();
      if (!world) return false;

      const targetValue = get(world, this.propertyPath);
      // Use copy method if the target value has one (for THREE.js Vector, Color, etc.)
      if (targetValue?.copy && typeof targetValue.copy === 'function') {
        targetValue.copy(value);
      } else {
        set(world, this.propertyPath, value);
      }
      return true;
    } else if (this.resourcePath) {
      const resource = this.resolveResource();
      if (!resource) return false;

      const targetValue = get(resource, this.propertyPath);
      // Use copy method if the target value has one (for THREE.js Vector, Color, etc.)
      if (targetValue?.copy && typeof targetValue.copy === 'function') {
        targetValue.copy(value);
      } else {
        set(resource, this.propertyPath, value);
      }
      return true;
    } else if (this.materialPath) {
      const material = this.resolveMaterial();
      if (!material) return false;

      const targetValue = get(material, this.propertyPath);
      // Use copy method if the target value has one (for THREE.js Vector, Color, etc.)
      if (targetValue?.copy && typeof targetValue.copy === 'function') {
        targetValue.copy(value);
      } else {
        set(material, this.propertyPath, value);
      }
      return true;
    } else if (this.componentPath) {
      const component = this.resolveComponent();
      if (!component) return false;

      const targetValue = get(component, this.propertyPath);
      // Use copy method if the target value has one (for THREE.js Vector, Color, etc.)
      if (targetValue?.copy && typeof targetValue.copy === 'function') {
        targetValue.copy(value);
      } else {
        set(component, this.propertyPath, value);
      }
      return true;
    } else {
      const actor = this.resolveActor();
      if (!actor) return false;

      const targetValue = get(actor, this.propertyPath);
      // Use copy method if the target value has one (for THREE.js Vector, Color, etc.)
      if (targetValue?.copy && typeof targetValue.copy === 'function') {
        targetValue.copy(value);
      } else {
        set(actor, this.propertyPath, value);
      }
      return true;
    }
  }

  /**
   * Sets a property value on both the engine object and updates the editor node if selected
   */
  setValue(value: any, actionName?: string): boolean {
    return setValue(this, value, actionName);
  }

  /**
   * Resolves the accessor to the corresponding editor node
   */
  resolveEditorNode(): IObjectTreeNode | null {
    if (this.isInvalid) return null;
    return resolveAccessorToNode(this);
  }

  /**
   * Resolves the accessor to get the parent component accessor
   */
  resolveParentAccessor(): UniversalAccessor | null {
    if (this.isInvalid) return null;
    if (!this.componentPath) return null;

    const component = this.resolveComponent();
    if (!component) return null;

    const parent = component.parent;
    if (!parent) return null;

    const actor = this.resolveActor();
    if (!actor) return null;

    return UniversalAccessor.forComponent(actor.uuid, parent.uuid);
  }

  /**
   * Resolves the accessor to the owning actor
   */
  resolveOwningActorAccessor(): UniversalAccessor {
    const actor = this.resolveActor();
    if (actor) {
      return UniversalAccessor.forActor(actor.uuid);
    }

    const component = this.resolveComponent();
    if (component) {
      return UniversalAccessor.forActor(component.getActor()?.uuid || '');
    }

    return UniversalAccessor.createInvalid();
  }

  /**
   * Resolves the accessor to get the object tree
   */
  resolveTree(): ISceneTree | null {
    return editorStore.scene.outliner as ISceneTree | null;
  }

  /**
   * Resolves the accessor to the corresponding tree node
   */
  resolveTreeNode(): IObjectTreeNode | null {
    return this.resolveEditorNode();
  }

  /**
   * Finds a component within an actor by UUID
   */
  private findComponentInActor(actor: ENGINE.Actor, componentId: string): ENGINE.SceneComponent | null {
    const findInComponent = (component: ENGINE.SceneComponent): ENGINE.SceneComponent | null => {
      if (component.uuid === componentId) return component;

      for (const child of component.children) {
        const found = findInComponent(child as ENGINE.SceneComponent);
        if (found) return found;
      }
      return null;
    };

    const rootComponent = actor.rootComponent;
    if (!rootComponent) return null;

    return findInComponent(rootComponent);
  }
}

/**
 * Creates a UniversalAccessor from an ObjectTreeNode
 */
export function createAccessorFromNode(node: IObjectTreeNode): UniversalAccessor {
  const actor = node.getActor();
  if (!actor) return UniversalAccessor.createInvalid();

  const actorId = actor.uuid;

  if (node.asActor) {
    // This is an actor node
    return UniversalAccessor.forActor(actorId);
  } else if (node.asComponent) {
    // This is a component node
    const componentId = node.asComponent.uuid;
    return UniversalAccessor.forComponent(actorId, componentId);
  }

  return UniversalAccessor.createInvalid(actorId);
}

/**
 * Creates a UniversalAccessor from an ObjectTreeNode with a specific property
 */
export function createAccessorFromNodeWithProperty(node: IObjectTreeNode, propertyPath: string): UniversalAccessor {
  const actor = node.getActor();
  if (!actor) return UniversalAccessor.createInvalid('', undefined, propertyPath);

  const actorId = actor.uuid;

  if (node.asActor) {
    // This is an actor node
    return UniversalAccessor.forActorProperty(actorId, propertyPath);
  } else if (node.asComponent) {
    // This is a component node
    const componentId = node.asComponent.uuid;
    return UniversalAccessor.forProperty(actorId, componentId, propertyPath);
  }

  return UniversalAccessor.createInvalid(actorId, undefined, propertyPath);
}

/**
 * Creates a UniversalAccessor from an ENGINE.Actor
 */
export function createAccessorFromActor(actor: ENGINE.Actor): UniversalAccessor {
  return UniversalAccessor.forActor(actor.uuid);
}

/**
 * Creates a UniversalAccessor from an ENGINE.SceneComponent
 */
export function createAccessorFromComponent(component: ENGINE.SceneComponent): UniversalAccessor {
  const actor = component.getActor();
  if (!actor) return UniversalAccessor.createInvalid('', component.uuid);

  return UniversalAccessor.forComponent(actor.uuid, component.uuid);
}

/**
 * Resolves a UniversalAccessor to an ObjectTreeNode if the object is currently selected
 */
export function resolveAccessorToNode(accessor: UniversalAccessor): IObjectTreeNode | null {
  const editableObject = editorStore.scene.selectedObjects.find(obj => obj.asActor?.uuid === accessor.actorId);
  if (!editableObject) return null;

  if (!accessor.componentPath) {
    // Looking for the actor node
    return editableObject.hierarchy.rootNode;
  }

  // Looking for a component node
  return editableObject.hierarchy.getNodeById(accessor.componentPath) || null;
}

/**
 * Resolves a UniversalAccessor to the corresponding editor node
 */
export function resolveEditorNode(accessor: UniversalAccessor): IObjectTreeNode | null {
  return resolveAccessorToNode(accessor);
}

/**
 * Helper function to check if a target value can use the copy method
 */
function canUseCopyMethod(targetValue: any, value: any): boolean {
  // Use copy method if the target value has one (for THREE.js Vector, Color, etc.)
  return targetValue?.copy &&
    typeof targetValue.copy === 'function' &&
    value !== undefined &&
    value !== null &&
    typeof targetValue === 'object' &&
    typeof value === 'object';
}

/**
 * Helper function to check if a property has a setter on the object.
 * Traverses the prototype chain since getters/setters are often defined on prototypes.
 */
function hasSetter(object: any, propertyPath: string): boolean {
  // Get the parent object and final property name
  const parts = propertyPath.split('.');
  const propName = parts.pop();
  if (!propName) return false;

  // Get the parent object
  let parent = object;
  for (const part of parts) {
    if (parent === null || parent === undefined) return false;
    parent = parent[part];
  }
  if (parent === null || parent === undefined) return false;

  // Traverse the prototype chain to find the property descriptor
  let current = parent;
  while (current !== null) {
    const descriptor = Object.getOwnPropertyDescriptor(current, propName);
    if (descriptor !== undefined) {
      return typeof descriptor.set === 'function';
    }
    current = Object.getPrototypeOf(current);
  }

  return false;
}

/**
 * Helper function to safely set a value using copy method with fallback.
 * If the property has a setter, the setter is called instead of using .copy()
 * to ensure any side effects in the setter are executed.
 */
function safeSetValue(targetValue: any, value: any, object: any, propertyPath: string, context: string): void {
  // If the property has a setter, use it directly (even if the value has copy method)
  // This ensures setter side effects (like attachLightTarget) are executed
  if (hasSetter(object, propertyPath)) {
    set(object, propertyPath, value);
    return;
  }

  if (canUseCopyMethod(targetValue, value)) {
    try {
      targetValue.copy(value);
    } catch (error) {
      console.warn(`[UniversalAccessor] Copy method failed for ${context} property ${propertyPath}:`, error);
      set(object, propertyPath, value);
    }
  } else {
    set(object, propertyPath, value);
  }
}

/**
 * Sets a world property value
 */
function setWorldPropertyValue(accessor: UniversalAccessor, value: any): { success: boolean; oldValue: any } {
  const world = accessor.resolveWorld();
  if (!world || !accessor.propertyPath) {
    return { success: false, oldValue: null };
  }

  const targetValue = get(world, accessor.propertyPath);
  const oldValue = targetValue;

  safeSetValue(targetValue, value, world, accessor.propertyPath, 'world');

  return { success: true, oldValue };
}

/**
 * Sets a resource property value
 */
function setResourcePropertyValue(accessor: UniversalAccessor, value: any): { success: boolean; oldValue: any } {
  const resource = accessor.resolveResource();
  if (!resource || !accessor.propertyPath) {
    return { success: false, oldValue: null };
  }

  const targetValue = get(resource, accessor.propertyPath);
  const oldValue = targetValue;

  // Special handling for color properties - convert THREE.Color to engine format [r, g, b, 'c']
  let valueToSet = value;
  if (value && typeof value === 'object' && 'isColor' in value && value.isColor) {
    // Check if the target is an array (engine color format)
    if (Array.isArray(targetValue) && targetValue.length >= 3) {
      valueToSet = [value.r, value.g, value.b, 'c'];
    }
  }

  safeSetValue(targetValue, valueToSet, resource, accessor.propertyPath, 'resource');

  // Mark the ResourceData wrapper as dirty for saving (need to find it in property store)
  const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
  if (inspectorPropertyStore && accessor.resourcePath) {
    const resourceSource = inspectorPropertyStore.sources.find(source => {
      if (source.editable && 'path' in source.editable &&
        source.target && typeof source.target === 'object' &&
        'instance' in source.target) {
        return source.editable.path === accessor.resourcePath;
      }
      return false;
    });
    if (resourceSource && resourceSource.target) {
      (resourceSource.target as any).isDirty = true;
    }
  }

  return { success: true, oldValue };
}

/**
 * Sets a material property value
 */
function setMaterialPropertyValue(accessor: UniversalAccessor, value: any): { success: boolean; oldValue: any } {
  const material = accessor.resolveMaterial();
  if (!material || !accessor.propertyPath) {
    return { success: false, oldValue: null };
  }

  const targetValue = get(material, accessor.propertyPath);
  const oldValue = targetValue;

  safeSetValue(targetValue, value, material, accessor.propertyPath, 'material');

  // Mark material as dirty for saving
  if (material.userData) {
    material.userData.isDirty = true;
  } else {
    material.userData = { isDirty: true };
  }

  return { success: true, oldValue };
}

/**
 * Sets a component property value
 */
function setComponentPropertyValue(accessor: UniversalAccessor, value: any): { success: boolean; oldValue: any } {
  const component = accessor.resolveComponent();
  if (!component || !accessor.propertyPath) {
    return { success: false, oldValue: null };
  }

  const targetValue = get(component, accessor.propertyPath);
  const oldValue = targetValue;

  safeSetValue(targetValue, value, component, accessor.propertyPath, 'component');

  return { success: true, oldValue };
}

/**
 * Sets an actor property value
 */
function setActorPropertyValue(accessor: UniversalAccessor, value: any): { success: boolean; oldValue: any } {
  const actor = accessor.resolveActor();
  if (!actor || !accessor.propertyPath) {
    return { success: false, oldValue: null };
  }

  const targetValue = get(actor, accessor.propertyPath);
  const oldValue = targetValue;

  safeSetValue(targetValue, value, actor, accessor.propertyPath, 'actor');

  return { success: true, oldValue };
}

/**
 * Sets a property value on both the engine object and updates the editor node if selected
 */
export function setValue(accessor: UniversalAccessor, value: any, actionName?: string): boolean {
  if (accessor.isInvalid) return false;
  if (!accessor.propertyPath) return false;

  let success = false;
  let oldValue: any = null;

  // Set the value on the engine object
  if (accessor.isWorld) {
    const result = setWorldPropertyValue(accessor, value);
    success = result.success;
    oldValue = result.oldValue;
  } else if (accessor.resourcePath) {
    const result = setResourcePropertyValue(accessor, value);
    success = result.success;
    oldValue = result.oldValue;
  } else if (accessor.materialPath) {
    const result = setMaterialPropertyValue(accessor, value);
    success = result.success;
    oldValue = result.oldValue;
  } else if (accessor.componentPath) {
    const result = setComponentPropertyValue(accessor, value);
    success = result.success;
    oldValue = result.oldValue;
  } else {
    const result = setActorPropertyValue(accessor, value);
    success = result.success;
    oldValue = result.oldValue;
  }

  if (!success) return false;
  editorStore.scene.bumpMissingAssetDiagnosticsEpoch();

  // Update the editor node if this object is currently selected
  // Use the same logic as updatePropertyUI function to properly update the properties panel
  if (accessor.isWorld) {
    // Handle world properties - find and update the UI property
    const world = accessor.resolveWorld();
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (world && inspectorPropertyStore && accessor.propertyPath) {
      // Find the property in the inspector's property store
      const property = inspectorPropertyStore.findPropertyByPath(accessor.propertyPath);
      if (property && 'setValue' in property && typeof property.setValue === 'function') {
        // Update the UI property with fromPanel=false to refresh the display
        try {
          // Cast to any to handle different property types with different setValue signatures
          (property as any).setValue(value, false);
        } catch (error) {
          // If setValue fails due to type mismatch, just log it - the underlying world was still updated
          console.debug(`[UniversalAccessor] Failed to update UI property ${accessor.propertyPath}:`, error);
        }
      }
    }
  } else if (accessor.resourcePath) {
    // Handle resource properties - find and update the UI property
    const resource = accessor.resolveResource();
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (resource && inspectorPropertyStore && accessor.propertyPath) {
      // Find the property in the inspector's property store
      const property = inspectorPropertyStore.findPropertyByPath(accessor.propertyPath);
      if (property && 'setValue' in property && typeof property.setValue === 'function') {
        // Update the UI property with fromPanel=false to refresh the display
        try {
          // Cast to any to handle different property types with different setValue signatures
          (property as any).setValue(value, false);
        } catch (error) {
          // If setValue fails due to type mismatch, just log it - the underlying resource was still updated
          console.debug(`[UniversalAccessor] Failed to update UI property ${accessor.propertyPath}:`, error);
        }
      }
    }
  } else if (accessor.materialPath) {
    // Handle material properties - find and update the UI property
    const material = accessor.resolveMaterial();
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (material && inspectorPropertyStore && accessor.propertyPath) {
      // Find the property in the inspector's property store
      const property = inspectorPropertyStore.findPropertyByPath(accessor.propertyPath);
      if (property && 'setValue' in property && typeof property.setValue === 'function') {
        // Update the UI property with fromPanel=false to refresh the display
        try {
          // Cast to any to handle different property types with different setValue signatures
          (property as any).setValue(value, false);
        } catch (error) {
          // If setValue fails due to type mismatch, just log it - the underlying material was still updated
          console.debug(`[UniversalAccessor] Failed to update UI property ${accessor.propertyPath}:`, error);
        }
      }
    }
  } else {
    // Handle actor/component properties
    const firstSelectedObject = editorStore.scene.selectedObjects[0];
    if (firstSelectedObject && firstSelectedObject.propertyStore) {
      const selectedActor = firstSelectedObject.asActor;
      const selectedSubobjectTarget = firstSelectedObject.selectedSubobjectTarget;
      const targetObject = accessor.componentPath ? accessor.resolveComponent() : accessor.resolveActor();

      let isTargetOfSelectedObject = false;

      if (targetObject === selectedActor || targetObject === selectedSubobjectTarget) {
        // Target is the actor itself or the currently selected subobject
        isTargetOfSelectedObject = true;
      } else if (selectedActor && targetObject && typeof targetObject === 'object' && 'uuid' in targetObject) {
        // Check if target is the root component of the selected actor
        const rootComponent = selectedActor.rootComponent;
        if (rootComponent && targetObject.uuid === rootComponent.uuid) {
          isTargetOfSelectedObject = true;
        }
      }

      // If target belongs to selected object, update the UI
      if (isTargetOfSelectedObject) {
        DEBUG.logSetUIProperty && console.log('setting UI property value', accessor.propertyPath!, value);
        firstSelectedObject.setUIPropertyValue(accessor.propertyPath!, value);
      }

      // Notify the editable object of the property change
      if (isTargetOfSelectedObject && targetObject) {
        const changeDescription = actionName || `Set ${accessor.propertyPath}`;
        const valueDescription = oldValue !== null ? `${oldValue} -> ${value}` : `to ${value}`;
        firstSelectedObject.notifyPropertyChanged(changeDescription, valueDescription, targetObject);
      }
    }
  }

  return true;
}

/**
 * Utility function to check if an accessor is currently accessible (object exists in world)
 */
export function isAccessorValid(accessor: UniversalAccessor): boolean {
  if (accessor.isInvalid) return false;

  if (accessor.isWorld) {
    return accessor.resolveWorld() !== null;
  } else if (accessor.resourcePath) {
    return accessor.resolveResource() !== null;
  } else if (accessor.materialPath) {
    return accessor.resolveMaterial() !== null;
  } else if (accessor.componentPath) {
    return accessor.resolveComponent() !== null;
  }
  return accessor.resolveActor() !== null;
}

/**
 * Directly sets a property value on a target object without creating a UniversalAccessor.
 * This is the fast path for Silent actions (e.g. drag): skips accessor construction,
 * undo/redo history, old-value capture, and inspector panel re-sync.
 * Uses the same safeSetValue logic (copy-method awareness) as the full path.
 */
export function setPropertyValueDirect(target: any, path: string, value: any): boolean {
  if (!target || !path) return false;
  const currentValue = get(target, path);
  if (currentValue === value) return false;
  safeSetValue(currentValue, value, target, path, 'direct');

  if (target.isMaterial || target.constructor?.name?.endsWith('Material')) {
    target.needsUpdate = true;
    if (!target.userData) {
      target.userData = {};
    }
    target.userData.isDirty = true;
  }

  return true;
}

/**
 * Creates a UniversalAccessor from a target object and property path
 */
export function createAccessorFromTarget(target: any, path: string): UniversalAccessor {
  if (!target) {
    console.warn('[UniversalAccessor] Cannot create accessor - target missing');
    return UniversalAccessor.createInvalid('', undefined, path);
  }

  // Check if target is a World (check constructor name first)
  if (target.constructor?.name === 'World' ||
    (typeof target === 'object' && 'scene' in target && 'inputManager' in target && 'timerSystem' in target)) {
    return UniversalAccessor.forWorldProperty(path);
  }

  // Check if target is a Resource instance (ENGINE.Resource subclass)
  if (target instanceof ENGINE.Resource) {
    // First try to get the asset path from the instance itself (stored when loaded)
    if ('__editorAssetPath' in target && typeof target.__editorAssetPath === 'string') {
      return UniversalAccessor.forResourceProperty(target.__editorAssetPath, path);
    }

    // Fallback: try to find the asset path from the property store
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (inspectorPropertyStore) {
      const resourceSource = inspectorPropertyStore.sources.find(source => source.target === target);
      if (resourceSource && resourceSource.editable && 'path' in resourceSource.editable) {
        return UniversalAccessor.forResourceProperty(resourceSource.editable.path, path);
      }
    }
    console.warn('[UniversalAccessor] Cannot create accessor - Resource instance found but no matching asset path');
    return UniversalAccessor.createInvalid('', undefined, path);
  }

  // Check if target is a ResourceData object (has 'class' and 'instance' properties)
  if (typeof target === 'object' && 'class' in target && 'instance' in target) {
    // This is a ResourceData object - need to get the asset path from the property store
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (inspectorPropertyStore) {
      const resourceSource = inspectorPropertyStore.sources.find(source => source.target === target);
      if (resourceSource && resourceSource.editable && 'path' in resourceSource.editable) {
        return UniversalAccessor.forResourceProperty(resourceSource.editable.path, path);
      }
    }
    console.warn('[UniversalAccessor] Cannot create accessor - ResourceData found but no matching asset path');
    return UniversalAccessor.createInvalid('', undefined, path);
  }

  if (!target.uuid) {
    console.warn('[UniversalAccessor] Cannot create accessor - target has no uuid');
    return UniversalAccessor.createInvalid('', undefined, path);
  }

  // Check if target is an Actor (or any subclass of Actor)
  if (target instanceof ENGINE.Actor) {
    return UniversalAccessor.forActorProperty(target.uuid, path);
  }

  // Check if target is a SceneComponent (or any subclass of SceneComponent)
  if (target instanceof ENGINE.SceneComponent) {
    const actor = target.getActor();
    if (!actor) {
      console.warn('[UniversalAccessor] Cannot create accessor - component has no actor');
      return UniversalAccessor.createInvalid('', target.uuid, path);
    }
    return UniversalAccessor.forProperty(actor.uuid, target.uuid, path);
  }

  // Check if target is a THREE.Material — resolve by asset path so the accessor stays
  // valid across undo/redo, which may reload the material and assign a new Three.js uuid.
  if (target.isMaterial || target.constructor?.name?.endsWith('Material')) {
    // Fast path: asset path stamped onto the material instance at load time.
    if ('__editorAssetPath' in target && typeof target.__editorAssetPath === 'string') {
      return UniversalAccessor.forMaterialProperty(target.__editorAssetPath, path);
    }

    // Fallback: find the matching source in the inspector property store.
    const inspectorPropertyStore = editorStore.inspectorPanel.propertyStore;
    if (inspectorPropertyStore) {
      const materialSource = inspectorPropertyStore.sources.find(source =>
        source.target === target &&
        source.editable != null && 'path' in source.editable
      );
      const assetPath = materialSource?.editable != null && 'path' in materialSource.editable
        ? (materialSource.editable as { path: string }).path
        : undefined;
      if (assetPath) {
        return UniversalAccessor.forMaterialProperty(assetPath, path);
      }
    }

    console.warn('[UniversalAccessor] Cannot create accessor - Material instance found but no matching asset path');
    return UniversalAccessor.createInvalid('', undefined, path);
  }

  // Check if target is a THREE.Object3D - try to find its associated actor
  if (target.constructor?.name === 'Object3D' || target.isObject3D) {
    const actor = findActorFromObject(target);
    if (!actor) {
      console.warn('[UniversalAccessor] Cannot create accessor - THREE.Object3D has no associated actor');
      return UniversalAccessor.createInvalid('', target.uuid, path);
    }
    return UniversalAccessor.forProperty(actor.uuid, target.uuid, path);
  }

  console.warn(`[UniversalAccessor] Cannot create accessor - unknown target type: ${target.constructor?.name}`);
  return UniversalAccessor.createInvalid('', undefined, path);
}

/**
 * Utility function to get a human-readable description of what an accessor points to
 */
export function getAccessorDescription(accessor: UniversalAccessor): string {
  if (accessor.isWorld) {
    const world = accessor.resolveWorld();
    if (!world) return 'Unknown World';

    if (!accessor.propertyPath) {
      return 'World';
    }

    return `World.${accessor.propertyPath}`;
  }

  if (accessor.resourcePath) {
    const resource = accessor.resolveResource();
    if (!resource) return `Unknown Resource (${accessor.resourcePath})`;

    const resourceClass = resource.class || 'Unnamed Resource';

    if (!accessor.propertyPath) {
      return resourceClass;
    }

    return `${resourceClass}.${accessor.propertyPath}`;
  }

  if (accessor.materialPath) {
    const material = accessor.resolveMaterial();
    if (!material) return `Unknown Material (${accessor.materialPath})`;

    const materialName = material.name || material.type || 'Unnamed Material';

    if (!accessor.propertyPath) {
      return materialName;
    }

    return `${materialName}.${accessor.propertyPath}`;
  }

  const actor = accessor.resolveActor();
  if (!actor) return `Unknown Actor (${accessor.actorId})`;

  const actorName = actor.name || 'Unnamed Actor';

  if (!accessor.componentPath) {
    return actorName;
  }

  const component = accessor.resolveComponent();
  if (!component) return `${actorName} - Unknown Component (${accessor.componentPath})`;

  const componentName = component.name || 'Unnamed Component';

  if (!accessor.propertyPath) {
    return `${actorName}.${componentName}`;
  }

  return `${actorName}.${componentName}.${accessor.propertyPath}`;
}